import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  fetchFeedbackTemplates,
  fetchFeedbackTemplateSuggestion,
  type FeedbackTemplateSuggestion,
  type LeverFeedbackTemplate,
} from '../../services/leverApi';

/**
 * The Lever feedback form attached to interviews booked through this link.
 *
 * Why this field has to exist at all: Lever attaches a feedback form when an
 * interview is SCHEDULED, and the candidate booking page bypasses Lever's own
 * scheduling dialog. Without a form chosen here, the interview reaches Lever with
 * nothing for the interviewer to fill in and the feedback link on the calendar
 * invite opens an empty interview.
 *
 * Prefilled from what previous interviews for the same posting+stage used, which
 * a sample of ~100 real interviews showed is right for 59 of 76 posting+stage
 * pairs. The rest genuinely differ, so the suggestion is applied only to an EMPTY
 * field and never overwrites a choice already made.
 */
/** How many matches the list shows at once — Lever holds hundreds of forms. */
const MAX_SUGGESTIONS = 40;

const templateLabel = (t: LeverFeedbackTemplate) => (t.group ? `${t.group} — ${t.name}` : t.name);

export function FeedbackFormSelect({
  value,
  postingId,
  stageId,
  label = 'Feedback form',
  onChange,
}: {
  value: string;
  postingId: string;
  /**
   * Empty means "no history to learn from" — the 🏁 round template has two of
   * these fields (final interviewer, bar raiser) and no Lever stage behind
   * either, so the picker is offered without a suggestion rather than not at
   * all. An empty stage would otherwise ask Lever what the last interviews for
   * stage "" used, and any answer to that is noise.
   */
  stageId: string;
  /** The round template needs two of these on one form, so they must be tellable apart. */
  label?: string;
  onChange: (templateId: string) => void;
}) {
  const [templates, setTemplates] = useState<LeverFeedbackTemplate[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [suggestion, setSuggestion] = useState<FeedbackTemplateSuggestion | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLLabelElement>(null);

  // The current value and callback, readable inside the suggestion effect
  // without making that effect depend on them — otherwise picking a form re-runs
  // the lookup and the prefill races the choice that was just made.
  //
  // useLayoutEffect, not useEffect: a suggestion that resolves after the commit
  // but before a passive effect runs would read a stale `valueRef` and overwrite
  // the selection. Layout effects run synchronously at commit, closing that gap.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  useLayoutEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  // The ref sync alone is not enough. If the suggestion lands between the user's
  // change event and the parent re-rendering with the new value, BOTH refs are
  // still stale and the prefill wins a race it must always lose. This flag is set
  // synchronously inside the event handler, so a human choice is never overwritten
  // no matter when the request comes back.
  const touchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void fetchFeedbackTemplates().then((res) => {
      if (cancelled) return;
      setTemplates(res.templates);
      setUnavailable(!res.available);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // No guard on an empty postingId/stageId here on purpose:
    // fetchFeedbackTemplateSuggestion already answers null without a request,
    // and a second copy of that rule is one that can drift. The 🏁 round
    // template passes no stage and simply gets no suggestion.
    void fetchFeedbackTemplateSuggestion(postingId, stageId).then((found) => {
      if (cancelled) return;
      // Always assigned, including null: a suggestion left over from the previous
      // posting would otherwise be shown as evidence for the current one.
      setSuggestion(found);
      // Only ever fills a blank, and never after the TA manager has chosen. Editing
      // an existing link must not have its form silently swapped for whatever
      // history happens to favour.
      if (found && !valueRef.current && !touchedRef.current) {
        onChangeRef.current(found.templateId);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [postingId, stageId]);

  // Close the list on an outside click. Same shape as ChannelPicker: the modal
  // around this field stays open, so a stray list left hanging over the form is
  // the failure mode.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const chosenIsSuggested = Boolean(suggestion && value === suggestion.templateId);

  const chosen = templates.find((t) => t.id === value) || null;
  const disabled = !loaded || templates.length === 0;
  /**
   * Every whitespace-separated word must appear somewhere in the label, so
   * "eng case" finds "Engineering — Case study". Substring-on-the-whole-query
   * would miss it, and with hundreds of forms in Lever the search is the only
   * way anyone finds one (Theresa, Sep 2026).
   */
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (words.length === 0 ? templates : templates.filter((t) => {
    const hay = templateLabel(t).toLowerCase();
    return words.every((w) => hay.includes(w));
  })).slice(0, MAX_SUGGESTIONS);

  const choose = (templateId: string) => {
    touchedRef.current = true;
    onChange(templateId);
    setQuery('');
    setOpen(false);
  };

  return (
    <label className="block" ref={rootRef}>
      <span className="mb-1 block text-xs font-medium text-gray-700">{label}</span>
      <div className="relative">
        <input
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          // Shows what is CHOSEN when idle and what is being TYPED when
          // searching — one field, so there is no "search box plus a separate
          // value" to keep in step.
          value={open ? query : chosen ? templateLabel(chosen) : ''}
          placeholder={
            disabled
              ? 'Loading feedback forms…'
              : chosen
                ? 'Type to search…'
                : 'No feedback form — type to search…'
          }
          onFocus={() => {
            setQuery('');
            setOpen(true);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setOpen(false);
            if (e.key === 'Enter' && open && matches.length > 0) {
              e.preventDefault();
              choose(matches[0].id);
            }
          }}
          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
        />
        {open && !disabled && (
          <ul
            role="listbox"
            className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
          >
            {/* Clearing is a choice like any other, so it lives in the list
                rather than as a separate button beside the field. */}
            {/*
              The OPTION role sits on the list item itself, not on a button
              inside it: a listbox's options must be its direct children, and
              nesting a control in between takes them out of the accessibility
              tree (they stopped being findable by role at all). The input keeps
              focus throughout, which is the standard combobox arrangement — the
              options are never focused themselves.
            */}
            <li
              role="option"
              aria-selected={value === ''}
              className="cursor-pointer px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-50"
              // mousedown, not click: it fires before the input's blur
              onMouseDown={(e) => {
                e.preventDefault();
                choose('');
              }}
            >
              No feedback form
            </li>
            {matches.map((t) => (
              <li
                key={t.id}
                role="option"
                aria-selected={t.id === value}
                className="cursor-pointer px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                onMouseDown={(e) => {
                  e.preventDefault();
                  choose(t.id);
                }}
              >
                {templateLabel(t)}
              </li>
            ))}
            {words.length > 0 && matches.length === 0 && (
              <li className="px-3 py-1.5 text-sm text-gray-500">No form matches “{query.trim()}”.</li>
            )}
          </ul>
        )}
      </div>

      {unavailable ? (
        <span className="mt-1 block text-xs text-[#854F0B]">
          Could not load feedback forms from Lever. Interviews booked through this link will have no
          feedback form until one is chosen.
        </span>
      ) : chosenIsSuggested ? (
        <span className="mt-1 block text-xs text-gray-500">
          Suggested — {suggestion?.used} of the last {suggestion?.total} interviews for this role and
          stage used this form. Change it if that is wrong.
        </span>
      ) : (
        <span className="mt-1 block text-xs text-gray-500">
          What the interviewer fills in afterwards. Without one, the feedback link on the calendar
          invite opens an empty interview.
        </span>
      )}
    </label>
  );
}
